import {
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import {useFormContext} from 'react-hook-form'
import classNames from 'classnames'
import {t} from 'i18next'

import {ReactComponent as UploadFileIcon} from '../../assets/icons/upload-file.svg'
import {ReactComponent as UploadFileSuccessIcon} from '../../assets/icons/upload-file-success.svg'
import Card from '../Card/Card'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'

import styles from './UploadFile.module.scss'

interface IUploadFile {
  className?: string
  maxSize?: number
  title: string | null
  label?: string | null
  name?: string | null
  allowedFormats: string[]
  externalErrorMessage?: string | null
  onFileUploaded: (file: any) => Promise<boolean>
  uploadFileIcon?: ReactNode
}

export type UploadFileHandle = {
  clearFile: () => void
  onClickFileInput: () => void
}

const UploadFile: ForwardRefRenderFunction<UploadFileHandle, IUploadFile> = (
  {
    className,
    allowedFormats,
    maxSize = 5, // mb
    title,
    label,
    name,
    externalErrorMessage,
    uploadFileIcon,
    onFileUploaded,
  },
  forwardedRef,
) => {
  const inputFileRef = useRef(null)
  useImperativeHandle(forwardedRef, () => ({
    clearFile() {
      setErrorMessage(null)
      setSuccess(false)
      setFileName('')
    },
    onClickFileInput() {
      ;(inputFileRef.current as HTMLElement | null)?.click()
    },
  }))

  const [fileName, setFileName] = useState<string>()
  const [errorMessage, setErrorMessage] = useState<string | null>()
  const [isUploading, setUploading] = useState<boolean>(false)
  const [isSuccess, setSuccess] = useState<boolean>(false)
  const parseFormatsLabel = useCallback(
    (formats: string[]): string => {
      return formats.map(format => format?.split('.').join('').toLocaleUpperCase()).join('/')
    },
    [allowedFormats],
  )
  const parseAccept = useCallback(
    (formats: string[]): string => {
      return formats.map(format => `.${format?.split('.').join('')}`.toLocaleLowerCase()).join(',')
    },
    [allowedFormats],
  )
  const onUploaded = useCallback(
    async (file: File) => {
      try {
        setUploading(true)
        setFileName((!!file && file.name) || '')
        let resultSuccess = false
        if (!!file) {
          if (file.size / 1024 / 1024 > maxSize) {
            setErrorMessage(`${t('uploadFile.maxSizeError')} ${maxSize} mb`)
            return
          } else !!errorMessage && setErrorMessage(null)

          const formData = new FormData()
          formData.append('file', file, file.name)
          formData.append('name', 'file')
          formData.append('filename', 'file')
          resultSuccess = await onFileUploaded(formData)
        } else {
          resultSuccess = await onFileUploaded(file)
          setErrorMessage(null)
        }
        setSuccess(resultSuccess)
      } catch (error) {
        console.log('Error uploading file')
      } finally {
        setUploading(false)
      }
    },
    [allowedFormats, maxSize, fileName, errorMessage, isUploading],
  )

  const {
    formState: {errors},
  } = (!!useFormContext() && useFormContext()) || {formState: {}}

  useEffect(() => {
    if (errorMessage || externalErrorMessage) setFileName('')
  }, [errorMessage, externalErrorMessage])

  return (
    <div className={styles.container}>
      {!!label && (
        <span
          className={classNames(styles.label, {
            [styles.error]: !!errors && !!errors[name || ''],
          })}>
          {label}
        </span>
      )}
      <Card
        className={classNames(styles.card, className, {
          [styles.error]:
            !!errorMessage || !!externalErrorMessage || (!!errors && !!errors[name || '']),
        })}
        withShadow>
        <div
          className={styles.uploader}
          onClick={() => document.getElementById('uploadFile')?.click()}
          onDragOver={(event: any) => event.preventDefault()}
          onDragEnter={(event: any) => event.preventDefault()}
          onDrop={(event: any) => {
            onUploaded(event.dataTransfer?.files[0])
            event.preventDefault()
          }}>
          <input
            id="uploadFile"
            type="file"
            accept={parseAccept(allowedFormats)}
            style={{display: 'none'}}
            onChange={(event: any) => onUploaded(event.target?.files[0])}
            ref={inputFileRef}
          />
          {isUploading ? (
            <LoadingSpinner />
          ) : isSuccess ? (
            <UploadFileSuccessIcon />
          ) : (
            (!!uploadFileIcon && uploadFileIcon) || <UploadFileIcon />
          )}
          <div className={styles.text}>
            {title && (
              <span className={styles.title}>
                {isUploading ? `${t('loading')}...` : isSuccess && fileName ? fileName : title}
              </span>
            )}
            <span className={styles.format}>{`${t('uploadFile.format')} ${parseFormatsLabel(
              allowedFormats,
            )} (Max. ${maxSize} mb)`}</span>
          </div>
        </div>
      </Card>
      <span
        className={classNames(styles.errorMessage, {
          [styles.active]:
            !!errorMessage || !!externalErrorMessage || (!!errors && !!errors[name || '']),
        })}>
        {errorMessage || externalErrorMessage || (!!errors && !!errors[name || ''])}
      </span>
    </div>
  )
}

export default forwardRef(UploadFile)
